import { useEffect, useMemo, useState } from 'react';
import { Content, PageWithHeader, Progress } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Box,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import SearchIcon from '@material-ui/icons/Search';
import { ViewMode } from '../shortcuts/types';
import { ViewToggle } from '../shortcuts/components';
import { DocsList } from './components/organisms/DocsList';
import {
  filterDocumentationEntities,
  getDocumentationEntities,
} from './docsUtils';
import type { Entity } from '@backstage/catalog-model';

export const DocsPage = () => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.GRID);
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchEntities = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const response = await catalogApi.getEntities({
          filter: { 'spec.type': 'library' },
        });
        setEntities(
          filterDocumentationEntities(
            getDocumentationEntities(response),
            '',
            'library',
          ),
        );
      } catch (fetchError: unknown) {
        setError(
          fetchError instanceof Error
            ? fetchError
            : new Error('No fue posible cargar la documentación de librerías.'),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchEntities();
  }, [catalogApi]);

  const filteredEntities = useMemo(
    () => filterDocumentationEntities(entities, searchQuery, 'library'),
    [entities, searchQuery],
  );

  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <div className={classes.root}>
      <PageWithHeader
        title="Libs"
        subtitle={`${entities.length} ${
          entities.length === 1
            ? 'librería con documentación'
            : 'librerías con documentación'
        }`}
        themeId="tool"
      >
        <Content className={classes.content}>
          {loading && <Progress />}
          {!loading && error && (
            <Typography color="error" role="alert">
              {error.message}
            </Typography>
          )}
          {!loading && !error && (
            <>
              <Box className={classes.toolbar}>
                <TextField
                  variant="outlined"
                  fullWidth
                  placeholder="Buscar librerías por nombre, descripción o tag"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  inputProps={{ 'aria-label': 'Buscar librerías' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: hasSearchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Limpiar búsqueda"
                          onClick={() => setSearchQuery('')}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
                <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
              </Box>

              <DocsList
                entities={filteredEntities}
                groupByType={false}
                viewMode={viewMode}
              />
            </>
          )}
        </Content>
      </PageWithHeader>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    overflowX: 'hidden',
    width: '100%',
    '& header': {
      backgroundColor: '#1A1F36 !important',
      backgroundImage: 'none !important',
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        color: '#00B7CF !important',
      },
    },
  },
  content: {
    boxSizing: 'border-box',
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    margin: '0 auto !important',
    minHeight: 0,
    padding: `${theme.spacing(4)}px ${theme.spacing(6)}px !important`,
    width: '100% !important',
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(2)}px !important`,
    },
  },
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(3.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
    },
  },
  searchSummary: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
  },
}));
